import {
  getProductListViewAllData,
  handleCustomization,
  summaryOptions,
  useChangeViewOptions,
  useShowSortingOption,
} from "@scenes/searchResults/Utils";

import React, { memo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { useIsFocused } from "@react-navigation/native";

import { useTheme } from "styled-components/native";
import { ProductCategory } from "@utils/constants";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import currency from "currency.js";
import Routes from "@navigations/Routes";
import SortingFiltersModal from "@scenes/searchResults/SortingFiltersModal";
import SavedSearchPopup from "@scenes/searchResults/SavedSearchPopup";

import CustomTopTabsWeb from "@components/molecules/CustomTopTabs/CustomTopTabsWeb";
import { Portal } from "@gorhom/portal";
import SearchResultSummaryBox from "@components/molecules/SearchResultSummaryBox";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useDispatch, useSelector } from "react-redux";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { useSearchFiltersPayload } from "@redux/slices/searchFilters";
import { ColumnStructure } from "../types";
import { useQueryClient } from "@tanstack/react-query";
import ArrangeListViewScreenModal from "../ArrangeListViewScreenModal";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import {
  ChangeViewMenuOptions,
  GemstoneChangeViewMenuOptions,
  initialProductArrayWeb,
  jewelryInitialProductArrayWeb,
} from "@utils/product/menuBuilders";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import { push, replace } from "@navigations/utils/RootNavigation";
import { filterSummaryData } from "@scenes/bulk-request/Utils";
import { useHideSummarySectionProduct } from "@redux/slices/globalSlice";

const ListHeaderComponentWeb = ({
  selectViewType,
  sortingFiltersParams,
  productType,
  masterCheckBoxSelection,
  selectedItems,
  selectedViewType,
  totalItems,
  totalItemsPages,
  setSortingFiltersParams,
  toggleFilterScreen,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  queryParamsList,
  masterCheckboxSelected,
  isFilterVisible,
  showDotOnFilter,
  selectOption,
  skipFilterScreenForWeb,
  isloading,
  setCustomizeColumnParams,
  enterSecondFlow,
}: any) => {
  const isFocused = useIsFocused();
  const userInfo = useUserInfo();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const { deviceWidth: screenWidth } = useIsFoldable();
  const productPrefix = useProductPrefix();
  const [openListViewDialog, setOpenListViewDialogDialog] = useState(false);
  const [openSortingFilterDialog, setOpenSortingFilterDialog] = useState(false);
  const [isSavedSearchPopupOpen, setIsSavedSearchPopupOpen] = useState(false);
  const isGuest = useSelector((state: any) => state.userInfo.isGuest);
  const hideSummarySectionProduct = useHideSummarySectionProduct();
  const hideSummarySection = hideSummarySectionProduct?.[productType] ?? false;

  const customSearchData = useCustomSearchData();
  const changeViewOptions = useChangeViewOptions(
    customSearchData[productType]?.header[`sub-sections`]?.change_view?.options,
    userInfo.getPriceMode(),
    t
  );

  const summarySectionData = filterSummaryData(
    sectionName === SearchFilterTypeEnum.PAIRS
      ? customSearchData[productType]?.summarySection?.pair?.options
      : sectionName === SearchFilterTypeEnum.PARCEL
      ? customSearchData[productType]?.summarySection?.parcels?.options
      : customSearchData[productType]?.summarySection?.options,
    userInfo.getPriceMode()
  );

  const showSortingOption = useShowSortingOption(
    customSearchData[productType]?.header[`sub-sections`]?.sorting,
    userInfo.getPriceMode()
  );

  const [productArray, setProductArray] = useState(
    productType === ProductCategory.JEWELRY
      ? jewelryInitialProductArrayWeb(t)
      : initialProductArrayWeb(t)
  );

  useEffect(() => {
    setProductArray(
      productType === ProductCategory.JEWELRY
        ? jewelryInitialProductArrayWeb(t)
        : initialProductArrayWeb(t)
    );
  }, [userInfo.user?.locale, productType]);

  const allowedItems = `${totalItemsPages ? 1 : 0}-${totalItemsPages}`;

  const selectAllText = userInfo.isIFrame()
    ? t("searchResult.showing.text", {
        x: allowedItems,
        y: currency(totalItems, {
          symbol: "",
          precision: 0,
        }).format(),
      })
    : t("searchResult.selectAll.text", {
        x: allowedItems,
        y: currency(totalItems, {
          symbol: "",
          precision: 0,
        }).format(),
      });

  const deSelectAllText = t("searchResult.deSelectAll.text", {
    x: allowedItems,
    y: currency(totalItems, {
      symbol: "",
      precision: 0,
    }).format(),
  });

  const changeViewMenuOptions =
    changeViewOptions.length > 0
      ? changeViewOptions
      : productType === ProductCategory.GEMSTONE
      ? GemstoneChangeViewMenuOptions(t)
      : ChangeViewMenuOptions(t);

  const deselectAllOptions = () => {
    const updatedProductArray = productArray.map((item) => ({
      ...item,
      selection: false,
    }));
    setProductArray(updatedProductArray);
  };

  const handleCardClick = (clickedItem: any) => {
    const updatedProductArray = productArray.map((item) =>
      item.id === clickedItem.id
        ? { ...item, selection: true }
        : { ...item, selection: false }
    );
    setProductArray(updatedProductArray);

    switch (clickedItem.eventName) {
      case SearchResultsAndFavoritesEnum.SAVE_SEARCH:
        if (isGuest) {
          dispatch(setLoginModalType("default"));
          throw new Error("Guest user is not authorized");
        } else {
          setIsSavedSearchPopupOpen(true);
        }
        break;
      case SearchResultsAndFavoritesEnum.FILTERS:
        if (userInfo.isIFrame()) {
          replace(Routes.FilterScreen, {
            productType,
            fromResultScreen: false,
            sectionName,
            productPrefix,
          });
          deselectAllOptions();
        } else if (theme.isDesktop || theme.isTablet) {
          toggleFilterScreen();
          deselectAllOptions();
        } else {
          push(Routes.FilterScreen, {
            productType,
            fromResultScreen: true,
            sectionName,
            productPrefix,
          });
        }
        break;
      case SearchResultsAndFavoritesEnum.CUSTOM_COLUMN:
        if (totalItems) {
          setOpenListViewDialogDialog(true);
        } else return;
        break;
      case SearchResultsAndFavoritesEnum.SORTING:
        if (totalItems) {
          setOpenSortingFilterDialog(true);
        } else return;
        break;
    }
  };

  const handleSavedSearchPopupShowChange = (show: boolean) => {
    setIsSavedSearchPopupOpen(show);
    deselectAllOptions();
  };

  const onSortingFiltersApplied = (paramList: string[]) => {
    setSortingFiltersParams((prev) => ({
      ...prev,
      [productType]: paramList, // Dynamically set values for the given productType
    }));
  };

  const currentListViewData = getProductListViewAllData();

  const onCustomizeColumnApplied = (paramList: ColumnStructure[]) => {
    handleCustomization(productType, paramList, dispatch, currentListViewData);
    setCustomizeColumnParams(paramList);
  };

  const onCustomizeColumnReset = (paramList: ColumnStructure[]) => {
    handleCustomization(productType, [], dispatch, currentListViewData);
    setCustomizeColumnParams(paramList);
    queryClient.resetQueries({ queryKey: ["SEARCH_RESULTS"] }).then();
  };

  const payload = useSearchFiltersPayload(productType);

  const checkIfJsonEmpty = (json: any, key: string) => {
    if (!json || !key) {
      return false;
    }
    const value = json[key];
    return value && Object.keys(value).length > 0;
  };
  let isEmptyFilter = checkIfJsonEmpty(payload, sectionName);
  return (
    <>
      {isFocused && (
        <Portal hostName={`TopBarPortal-${productType}`}>
          <CustomTopTabsWeb
            productArray={productArray}
            handleCardClick={handleCardClick}
            flatListBackgroundColor={theme["core-01"]}
            menuOptions={changeViewMenuOptions}
            defaultViewType={selectedViewType}
            selectViewType={selectViewType}
            menuItemEventName={[
              SearchResultsAndFavoritesEnum.CHANGE_VIEW,
              SearchResultsAndFavoritesEnum.BULK_ACTION,
            ]}
            sortingFiltersParams={sortingFiltersParams}
            showDotOnFilter={showDotOnFilter}
            masterCheckBoxSelection={masterCheckBoxSelection}
            masterCheckboxSelected={masterCheckboxSelected}
            selectAllText={selectAllText}
            deSelectAllText={deSelectAllText}
            isFilterVisible={isFilterVisible}
            productType={productType}
            totalItems={totalItems}
            skipFilterScreenForWeb={skipFilterScreenForWeb}
            isFilterApplied={isEmptyFilter}
            selectOption={selectOption}
            selectedItems={selectedItems}
            isloading={isloading}
            enterSecondFlow={enterSecondFlow}
            showChangeViewMenu={
              changeViewOptions.length > 1 &&
              Object.keys(
                customSearchData[productType]?.header[`sub-sections`]
                  ?.change_view?.options
              ).length > 1
            }
            sectionName={sectionName}
            showSortingOption={showSortingOption}
          />
        </Portal>
      )}
      {!isGuest && !hideSummarySection && selectedItems?.length > 0 && (
        <View
          style={{
            marginTop: SpaceType.Double,
            marginLeft: SpaceType.Double,
            marginRight: SpaceType.Double,
          }}
        >
          <SearchResultSummaryBox
            summaryOptions={summaryOptions(
              userInfo?.getPriceMode(),
              productType,
              !isCustomApp,
              summarySectionData
            )}
            selectedItems={selectedItems}
            productType={productType}
            tabletScreenWidth={screenWidth}
            selectOption={selectOption}
            sectionName={sectionName}
          />
        </View>
      )}

      <SortingFiltersModal
        isOpen={openSortingFilterDialog}
        onClose={() => {
          deselectAllOptions();
          setOpenSortingFilterDialog((open) => !open);
        }}
        productType={productType}
        onApply={onSortingFiltersApplied}
        selectedSortingFilters={sortingFiltersParams}
        sectionName={sectionName}
      />

      {isSavedSearchPopupOpen && (
        <SavedSearchPopup
          onSavedSearchPopupShowChange={handleSavedSearchPopupShowChange}
          queryParamList={queryParamsList}
          productType={productType}
        />
      )}

      <ArrangeListViewScreenModal
        isOpen={openListViewDialog}
        onClose={() => {
          deselectAllOptions();
          setOpenListViewDialogDialog((open) => !open);
        }}
        productType={productType}
        onApply={onCustomizeColumnApplied}
        onReset={onCustomizeColumnReset}
        selectedSortingFilters={sortingFiltersParams}
        backgroundColor={theme["core-02"]}
        sectionName={sectionName}
      />
    </>
  );
};

export default memo(ListHeaderComponentWeb);
